import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, Play, Radio, Zap, BellRing, RefreshCw, Trash2,
  Sparkles, ChevronRight, Loader2, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import {
  fetchChartinkDashboard, fetchChartinkSettings, saveChartinkSettings,
  chartinkScanNow, chartinkCheckNow, chartinkClearWatchlist, chartinkHistory,
  fetchChartinkScanners, addChartinkScanner, updateChartinkScanner, deleteChartinkScanner,
} from "../lib/api";

/* ═══════════════════════════════════════════════════════════════════════
   Screener — Awwwards-tier art direction.
   Editorial serif headlines, kinetic hero, numbered chapters, marquee.
   ═══════════════════════════════════════════════════════════════════════ */

const HERO_IMG = "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1800&q=85";
const CHART_IMG = "https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1400&q=85";

export default function Screener() {
  const [dash, setDash] = useState(null);
  const [settings, setSettings] = useState(null);
  const [scanners, setScanners] = useState([]);
  const [busy, setBusy] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const prevAlertCountRef = useRef(0);

  useEffect(() => {
    load();
    fetchChartinkSettings().then(setSettings).catch(() => {});
    loadScanners();
    chartinkHistory(7).then(r => setHistoryItems(r.alerts || [])).catch(() => {});
    const t = setInterval(load, 20_000);
    return () => clearInterval(t);
  }, []);

  const loadScanners = async () => {
    try {
      const r = await fetchChartinkScanners();
      setScanners(r.scanners || []);
    } catch { /* silent */ }
  };

  const load = async () => {
    try {
      const d = await fetchChartinkDashboard();
      // Detect new alerts arriving via poll → toast pop
      const prev = prevAlertCountRef.current;
      const cur = (d.alerts || []).length;
      if (prev !== 0 && cur > prev) {
        const fresh = (d.alerts || [])[0];
        toast.success(`Breakout · ${fresh.symbol} · ₹${fresh.breakout_price}`, {
          description: `15m high ₹${fresh.high_15m} → 5m close ₹${fresh.breakout_price}`,
          duration: 8000,
        });
      }
      prevAlertCountRef.current = cur;
      setDash(d);
    } catch { /* silent */ }
  };

  const runScan = async () => {
    setBusy(true);
    toast.loading("Pulling latest Chartink results…", { id: "scan" });
    try {
      const r = await chartinkScanNow();
      toast.success(`Scan complete · ${r.count} stocks · +${r.added} to watchlist`, { id: "scan" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed", { id: "scan" });
    } finally { setBusy(false); }
  };

  const runCheck = async () => {
    setBusy(true);
    toast.loading("Checking 5-minute candles…", { id: "chk" });
    try {
      const r = await chartinkCheckNow();
      toast.success(`Checked ${r.checked} · ${r.new_alerts} new breakout${r.new_alerts !== 1 ? "s" : ""}`, { id: "chk" });
      await load();
    } catch (e) { toast.error("Check failed", { id: "chk" }); }
    finally { setBusy(false); }
  };

  const clearWatch = async () => {
    if (!window.confirm("Clear today's watchlist & alerts?")) return;
    try {
      await chartinkClearWatchlist();
      toast.success("Watchlist cleared");
      load();
    } catch { toast.error("Failed to clear"); }
  };

  const saveScanner = async (payload) => {
    setBusy(true);
    try {
      const r = await addChartinkScanner(payload);
      toast.success(`Scanner added · ${r.slug} · ${r.count} stocks found`);
      await Promise.all([loadScanners(), load()]);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid scanner URL");
      return false;
    } finally { setBusy(false); }
  };

  const toggleScanner = async (slug, active) => {
    try {
      await updateChartinkScanner(slug, { active });
      toast.success(`${slug} · ${active ? "activated" : "paused"}`);
      loadScanners();
    } catch (e) { toast.error("Failed to update scanner"); }
  };

  const removeScanner = async (slug) => {
    if (!window.confirm(`Remove scanner "${slug}"?`)) return;
    try {
      await deleteChartinkScanner(slug);
      toast.success(`${slug} removed`);
      loadScanners();
    } catch (e) { toast.error("Failed to remove"); }
  };

  return (
    <main className="relative bg-[#0A0A0A] text-white">
      <TopNav />
      <Hero />
      <Marquee items={(dash?.watchlist || []).map(w => w.symbol)} slug={dash?.slug} />
      <Chapter02Dashboard
        dash={dash}
        onScan={runScan}
        onCheck={runCheck}
        onClear={clearWatch}
        busy={busy}
      />
      <Chapter03Manifesto />
      <Chapter04Alerts alerts={dash?.alerts || []} />
      <Chapter05History items={historyItems} />
      <Chapter06Settings scanners={scanners}
                         onSave={saveScanner}
                         onToggle={toggleScanner}
                         onRemove={removeScanner}
                         busy={busy} />
      <FooterBar dash={dash} />
    </main>
  );
}

/* ─────────────────────────  TOP NAV  ───────────────────────── */
function TopNav() {
  return (
    <motion.header
      initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-black/30 border-b border-white/6"
      data-testid="screener-topnav"
    >
      <div className="mx-auto max-w-[1600px] px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="topnav-home">
          <div className="h-8 w-8 rounded-full bg-white text-black flex items-center justify-center font-editorial text-lg italic">P</div>
          <div className="flex flex-col leading-none">
            <span className="font-editorial text-lg tracking-tight">PsychLevel</span>
            <span className="font-mono text-[9px] tracking-[0.3em] text-white/45 mt-1">SCREENER · N°02</span>
          </div>
        </Link>
        <div className="hidden md:flex items-center gap-10 font-mono text-[10px] tracking-[0.25em] uppercase text-white/60">
          <a href="#dashboard" className="hover:text-white transition-colors" data-testid="topnav-dashboard">02 · Dashboard</a>
          <a href="#manifesto" className="hover:text-white transition-colors">03 · Rules</a>
          <a href="#alerts" className="hover:text-white transition-colors">04 · Alerts</a>
          <a href="#history" className="hover:text-white transition-colors">05 · History</a>
          <a href="#settings" className="hover:text-white transition-colors">06 · Settings</a>
        </div>
        <Link to="/" className="pill-btn text-xs inline-flex items-center gap-2" data-testid="topnav-back">
          Back to lab <ArrowUpRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </motion.header>
  );
}

/* ─────────────────────────  HERO  ───────────────────────── */
function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.18]);
  const gridY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  const line1 = "Chartink signals,".split(" ");
  const line2 = "framed by the".split(" ");
  const line3 = "15-minute high.".split(" ");

  return (
    <section ref={ref} className="relative min-h-[100vh] flex items-end overflow-hidden pt-24 pb-16 lg:pb-24" data-testid="screener-hero">
      {/* editorial grid backdrop */}
      <motion.div
        style={{ y: gridY }}
        className="absolute inset-0 -z-10 opacity-[0.14] pointer-events-none"
        aria-hidden
      >
        <div className="absolute inset-0" style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }} />
      </motion.div>

      {/* Right-side parallax image */}
      <div className="absolute right-0 top-0 bottom-0 w-[46%] hidden lg:block -z-10">
        <motion.div style={{ y: imgY, scale: imgScale }} className="h-full w-full">
          <div className="absolute inset-0 bg-gradient-to-l from-transparent via-[#0A0A0A]/70 to-[#0A0A0A]" />
          <img
            src={HERO_IMG}
            alt="Trading terminal"
            className="h-full w-full object-cover object-center"
            style={{ filter: "grayscale(0.15) contrast(1.1) brightness(0.72)" }}
          />
        </motion.div>
      </div>

      {/* Left column content */}
      <div className="relative mx-auto max-w-[1600px] px-6 lg:px-10 w-full">
        {/* Chapter label */}
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="flex items-center gap-4 mb-10"
        >
          <span className="h-px w-14 bg-white/25" />
          <span className="font-mono text-[10px] tracking-[0.4em] uppercase text-white/55">
            Chapter 01 · The Screener
          </span>
        </motion.div>

        {/* Masked line-by-line reveal */}
        <h1 className="font-editorial text-[54px] sm:text-[76px] lg:text-[108px] leading-[0.96] font-medium tracking-[-0.035em] max-w-[900px]">
          <MaskedLine words={line1} delay={0.15} />
          <MaskedLine words={line2} delay={0.55} italic />
          <MaskedLine words={line3} delay={0.95} accent="text-[#00E5FF]" />
        </h1>

        {/* Deck */}
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.35, duration: 0.7 }}
          className="mt-10 max-w-xl text-base lg:text-lg text-white/60 leading-relaxed"
        >
          Every fifteen minutes, we harvest a Chartink screener, freeze the
          candle high, and stand watch. The moment a five-minute close prints
          above that line, you hear it — <span className="text-white">Telegram, toast, terminal</span>.
        </motion.p>

        {/* CTA row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.55, duration: 0.55 }}
          className="mt-12 flex flex-wrap items-center gap-5"
        >
          <a href="#dashboard" data-testid="hero-open-dashboard" className="pill-btn-primary inline-flex items-center gap-2">
            <Play className="h-4 w-4" strokeWidth={2.4} />
            Open live dashboard
          </a>
          <a href="#manifesto" data-testid="hero-read-manifesto" className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors font-mono text-xs tracking-widest uppercase">
            <span className="h-px w-8 bg-white/40" />
            Read the rulebook
          </a>
        </motion.div>

        {/* Signature moment: number ticker + live indicator */}
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8, duration: 0.6 }}
          className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-8 max-w-3xl border-t border-white/10 pt-8"
        >
          <MiniStat n="15" unit="min" label="scan cadence" />
          <MiniStat n="05" unit="min" label="breakout monitor" />
          <MiniStat n="24×7" unit="" label="watchlist memory" />
          <MiniStat n="03" unit="" label="alert channels" live />
        </motion.div>
      </div>
    </section>
  );
}

function MaskedLine({ words = [], delay = 0, italic = false, accent = "" }) {
  return (
    <span className={`block overflow-hidden ${italic ? "italic" : ""} ${accent}`}>
      <motion.span
        initial={{ y: "115%" }}
        animate={{ y: "0%" }}
        transition={{ delay, duration: 1.15, ease: [0.22, 1, 0.36, 1] }}
        className="inline-block"
      >
        {words.map((w, i) => (
          <span key={i} className="inline-block mr-[0.25em]">{w}</span>
        ))}
      </motion.span>
    </span>
  );
}

function MiniStat({ n, unit, label, live = false }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline gap-1">
        <span className="font-editorial text-4xl lg:text-5xl leading-none tracking-tight">{n}</span>
        {unit && <span className="font-mono text-xs text-white/45">{unit}</span>}
        {live && (
          <span className="inline-flex items-center gap-1 ml-2 font-mono text-[9px] tracking-[0.3em] uppercase text-[#39FF14]">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#39FF14] opacity-60" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#39FF14]" />
            </span>
            live
          </span>
        )}
      </div>
      <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-white/45">{label}</span>
    </div>
  );
}

/* ─────────────────────────  MARQUEE  ───────────────────────── */
function Marquee({ items = [], slug = "volume-breakout" }) {
  const showItems = items.length > 0 ? items : [
    "Volume", "Breakout", "Fifteen", "Minute", "High", "Watching",
    "Five", "Minute", "Close", "Signal", "Live",
  ];
  const doubled = [...showItems, ...showItems, ...showItems];
  return (
    <section className="relative overflow-hidden py-10 border-y border-white/8 bg-white/[0.015]" data-testid="marquee">
      <div className="flex" style={{ animation: "marq 50s linear infinite" }}>
        {doubled.map((w, i) => (
          <span key={i} className="flex items-center gap-6 pr-6 shrink-0">
            <span className="font-editorial italic text-4xl lg:text-6xl text-white/85">{w}</span>
            <Sparkles className="h-4 w-4 text-[#00E5FF]/60 shrink-0" />
          </span>
        ))}
      </div>
      <div className="absolute bottom-2 right-6 font-mono text-[10px] tracking-[0.4em] uppercase text-white/35">
        · source · chartink.com/{slug}
      </div>
      <style>{`@keyframes marq {from{transform:translateX(0)}to{transform:translateX(-33.333%)}}`}</style>
    </section>
  );
}

/* ─────────────────────────  DASHBOARD  ───────────────────────── */
function Chapter02Dashboard({ dash, onScan, onCheck, onClear, busy }) {
  const wl = dash?.watchlist || [];
  const alerts = dash?.alerts || [];
  const stocks_found = dash?.stocks_found ?? 0;
  const lastScan = dash?.last_scan_time_ist
    ? new Date(dash.last_scan_time_ist).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: false })
    : "—";

  return (
    <section id="dashboard" className="relative py-24 lg:py-36" data-testid="dashboard-section">
      <div className="mx-auto max-w-[1600px] px-6 lg:px-10">
        <ChapterHeader n="02" title="Dashboard" subtitle="Live watchlist · 5-minute breakout monitor" />

        {/* KPI strip */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-12">
          <BigStat label="Scanner last updated" value={lastScan} tone="cyan" testId="kpi-last-scan" />
          <BigStat label="Stocks found" value={stocks_found} tone="white" testId="kpi-stocks-found" />
          <BigStat label="On watchlist" value={dash?.watchlist_count ?? 0} tone="amber" testId="kpi-watchlist" />
          <BigStat label="Breakouts today" value={dash?.breakout_count ?? 0} tone="green" testId="kpi-breakouts" />
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-3 mb-8">
          <button onClick={onScan} disabled={busy} className="pill-btn-primary inline-flex items-center gap-2 disabled:opacity-50" data-testid="btn-scan-now">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Radio className="h-4 w-4" />}
            Scan Chartink now
          </button>
          <button onClick={onCheck} disabled={busy} className="pill-btn inline-flex items-center gap-2 text-sm" data-testid="btn-check-now">
            <RefreshCw className="h-4 w-4" /> Check 5m breakouts
          </button>
          <button onClick={onClear} className="pill-btn inline-flex items-center gap-2 text-sm text-white/70" data-testid="btn-clear-watchlist">
            <Trash2 className="h-4 w-4" /> Clear today
          </button>
        </div>

        {/* Two-column: watchlist + alert feed */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-8">
          {/* Watchlist */}
          <div>
            <div className="flex items-baseline justify-between mb-4">
              <h3 className="font-editorial text-3xl">Watchlist</h3>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/45">
                {wl.length} entries · today
              </div>
            </div>
            {wl.length === 0 ? (
              <EmptyState msg="No stocks in the watchlist yet — the next scan runs at :13 · :28 · :43 · :58 IST during market hours." />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {wl.map((w, i) => (
                  <WatchlistCard key={`${w.symbol}-${w.day_ist}`} w={w} delay={i * 0.03} />
                ))}
              </div>
            )}
          </div>

          {/* Alerts feed */}
          <div>
            <div className="flex items-baseline justify-between mb-4">
              <h3 className="font-editorial text-3xl">Alerts</h3>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/45">
                today · IST
              </div>
            </div>
            <div className="card-surface rounded-2xl p-2 max-h-[540px] overflow-y-auto" data-testid="alerts-feed">
              {alerts.length === 0 ? (
                <EmptyState msg="Nothing yet. When a 5-min candle closes above a stored 15-min high, it lands here." />
              ) : (
                <div className="divide-y divide-white/6">
                  {alerts.map((a, i) => (
                    <AlertRow key={i} a={a} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function BigStat({ label, value, tone = "white", testId }) {
  const cls = tone === "cyan" ? "text-[#00E5FF]" : tone === "green" ? "text-[#39FF14]" : tone === "amber" ? "text-[#FFB020]" : "text-white";
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
      className="card-surface rounded-2xl p-6"
      data-testid={testId}
    >
      <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-white/45">{label}</div>
      <div className={`mt-3 font-editorial text-5xl lg:text-6xl leading-none tracking-tight ${cls}`}>{value}</div>
    </motion.div>
  );
}

function WatchlistCard({ w, delay = 0 }) {
  const isBreakout = w.status === "breakout";
  const cur = w.current_price ?? w.price_at_scan;
  const diff = cur && w.high_15m ? ((cur - w.high_15m) / w.high_15m) * 100 : 0;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ delay, duration: 0.45 }}
      className={`card-surface rounded-xl p-4 border ${isBreakout ? "border-[#39FF14]/40 bg-[#39FF14]/[0.04]" : "border-white/8"}`}
      data-testid={`watchlist-row-${w.symbol}`}
    >
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <span className="ticker-badge">{w.symbol}</span>
          {isBreakout ? (
            <span className="ticker-badge text-[#39FF14] border-[#39FF14]/40 bg-[#39FF14]/12 animate-pulse-glow">✅ BREAKOUT</span>
          ) : (
            <span className="ticker-badge text-[#FFB020] border-[#FFB020]/30 bg-[#FFB020]/8">WAITING</span>
          )}
        </div>
        <span className="font-mono text-[10px] text-white/40">
          {w.source_scan_time ? new Date(w.source_scan_time).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: false }) : ""}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs font-mono">
        <div>
          <div className="text-white/40 text-[9px] tracking-widest uppercase">15m High</div>
          <div className="text-[#00E5FF] text-sm mt-0.5">₹{w.high_15m?.toLocaleString("en-IN")}</div>
        </div>
        <div className="text-right">
          <div className="text-white/40 text-[9px] tracking-widest uppercase">Current</div>
          <div className={`text-sm mt-0.5 ${diff >= 0 ? "text-[#39FF14]" : "text-white/70"}`}>
            ₹{cur ? cur.toLocaleString("en-IN") : "—"}
          </div>
        </div>
      </div>
      {isBreakout && (
        <div className="mt-2 pt-2 border-t border-white/10 font-mono text-[10px] text-[#39FF14]">
          5m close ₹{w.breakout_price?.toLocaleString("en-IN")} · +{w.diff_pct}% at {w.breakout_time_str}
        </div>
      )}
    </motion.div>
  );
}

function AlertRow({ a }) {
  const t = a.breakout_time_ist ? new Date(a.breakout_time_ist).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: false }) : "";
  return (
    <div className="flex items-start gap-3 px-3 py-3">
      <div className="mt-0.5 h-8 w-8 rounded-md bg-[#39FF14]/12 border border-[#39FF14]/30 flex items-center justify-center shrink-0">
        <BellRing className="h-4 w-4 text-[#39FF14]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="ticker-badge">{a.symbol}</span>
            <span className="font-editorial italic text-sm text-white/90">Breakout</span>
          </div>
          <span className="font-mono text-[10px] text-white/45">{t}</span>
        </div>
        <div className="mt-1 font-mono text-[11px] text-white/60">
          15m high <span className="text-[#00E5FF]">₹{a.high_15m}</span> · 5m close{" "}
          <span className="text-[#39FF14]">₹{a.breakout_price}</span> · +{a.diff_pct}%
        </div>
      </div>
    </div>
  );
}

function EmptyState({ msg }) {
  return (
    <div className="rounded-xl border border-dashed border-white/12 px-5 py-10 text-center text-sm text-white/50 font-mono" data-testid="empty-state">
      {msg}
    </div>
  );
}

/* ─────────────────────────  MANIFESTO  ───────────────────────── */
function Chapter03Manifesto() {
  const steps = [
    { n: "01", title: "Harvest", body: "Every 15 minutes, at :13 · :28 · :43 · :58 IST, we pull the Chartink scanner. Just before each 15-minute candle closes." },
    { n: "02", title: "Mark", body: "Each returned stock's 15-minute candle high is frozen in memory. That number is the invalidation line." },
    { n: "03", title: "Watch", body: "Every minute, we compare the latest completed 5-minute close against every stored high. Simple. Boring. Effective." },
    { n: "04", title: "Fire", body: "First close above the line triggers a Telegram DM, a website toast, and a row on the alerts feed. Once per stock per day." },
  ];
  return (
    <section id="manifesto" className="relative py-24 lg:py-36 border-t border-white/8" data-testid="manifesto-section">
      <div className="mx-auto max-w-[1600px] px-6 lg:px-10">
        <ChapterHeader n="03" title="The Rulebook" subtitle="Four moves. No more, no less." />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left: manifesto lead */}
          <div className="lg:col-span-5 lg:sticky lg:top-24 h-fit">
            <p className="font-editorial text-4xl lg:text-5xl leading-[1.05] tracking-tight text-white/90">
              A screener is a &nbsp;<span className="italic text-[#00E5FF]">hypothesis</span>. <br />
              A stop-loss is a &nbsp;<span className="italic">confession</span>. <br />
              The 15-minute high is neither — <br />
              it is <span className="underline decoration-1 underline-offset-8">the ledger</span>.
            </p>
            <div className="mt-8 relative rounded-2xl overflow-hidden">
              <img src={CHART_IMG} alt="Chart" className="w-full h-64 object-cover" style={{ filter: "grayscale(0.4) contrast(1.05)" }} />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0A0A0A]/70 via-transparent to-[#00E5FF]/10" />
              <div className="absolute bottom-3 left-4 font-mono text-[10px] tracking-[0.3em] uppercase text-white/70">plate n°01 · the ledger</div>
            </div>
          </div>

          {/* Right: numbered steps */}
          <div className="lg:col-span-7 space-y-8">
            {steps.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.55 }}
                className="grid grid-cols-[80px_1fr] gap-6 items-start border-t border-white/8 pt-8"
                data-testid={`manifesto-step-${s.n}`}
              >
                <div className="font-editorial italic text-6xl text-white/25">{s.n}</div>
                <div>
                  <h4 className="font-editorial text-3xl mb-2">{s.title}</h4>
                  <p className="text-white/60 text-sm lg:text-base leading-relaxed max-w-md">{s.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────  ALERTS SUMMARY  ───────────────────────── */
function Chapter04Alerts({ alerts }) {
  return (
    <section id="alerts" className="relative py-24 lg:py-36 border-t border-white/8" data-testid="alerts-section-04">
      <div className="mx-auto max-w-[1600px] px-6 lg:px-10">
        <ChapterHeader n="04" title="Where the alerts land" subtitle="Three channels. Same message. Zero delay." />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Channel n="01" title="Telegram" body="Full breakout card DM'd to your bot chat. Includes 15m high, 5m close, timestamp, and past win-rate." tone="cyan" testId="channel-telegram" />
          <Channel n="02" title="On-site toast" body="A live pop-up on this page the instant the backend detects a breakout — no refresh required." tone="green" testId="channel-toast" />
          <Channel n="03" title="History log" body="Every alert becomes a row below with date, symbol, level, breakout time and the eventual outcome." tone="amber" testId="channel-history" />
        </div>
        <div className="mt-10 font-mono text-[10px] tracking-[0.3em] uppercase text-white/40">
          WhatsApp channel · queued for your provider credentials (Twilio / Meta / AiSensy)
        </div>
      </div>
    </section>
  );
}

function Channel({ n, title, body, tone, testId }) {
  const cls = tone === "cyan" ? "text-[#00E5FF]" : tone === "green" ? "text-[#39FF14]" : "text-[#FFB020]";
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ duration: 0.55 }}
      className="card-surface rounded-2xl p-7"
      data-testid={testId}
    >
      <div className={`font-editorial italic text-6xl ${cls} opacity-30`}>{n}</div>
      <h4 className="mt-4 font-editorial text-3xl">{title}</h4>
      <p className="mt-3 text-white/55 text-sm leading-relaxed">{body}</p>
    </motion.div>
  );
}

/* ─────────────────────────  HISTORY  ───────────────────────── */
function Chapter05History({ items }) {
  return (
    <section id="history" className="relative py-24 lg:py-36 border-t border-white/8" data-testid="history-section">
      <div className="mx-auto max-w-[1600px] px-6 lg:px-10">
        <ChapterHeader n="05" title="Ledger" subtitle="Past 7 days of breakouts fired" />
        <div className="card-surface rounded-2xl overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-5 py-3 border-b border-white/8 text-[10px] font-mono tracking-widest uppercase text-white/45">
            <div className="col-span-2">Date</div>
            <div className="col-span-2">Symbol</div>
            <div className="col-span-2 text-right">15m High</div>
            <div className="col-span-2 text-right">5m Close</div>
            <div className="col-span-2 text-right">Δ</div>
            <div className="col-span-2 text-right">Time (IST)</div>
          </div>
          {items.length === 0 ? (
            <div className="px-6 py-12 text-center text-sm text-white/45" data-testid="history-empty">
              No history yet. Every breakout fired will be recorded here.
            </div>
          ) : items.map((h, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
              transition={{ delay: i * 0.02 }}
              className="grid grid-cols-12 gap-2 px-5 py-3 border-b border-white/5 last:border-b-0 items-center table-row-hover"
            >
              <div className="col-span-2 font-mono text-xs text-white/60">{h.day_ist}</div>
              <div className="col-span-2"><span className="ticker-badge">{h.symbol}</span></div>
              <div className="col-span-2 text-right font-mono text-sm text-[#00E5FF]">₹{h.high_15m}</div>
              <div className="col-span-2 text-right font-mono text-sm text-[#39FF14]">₹{h.breakout_price}</div>
              <div className="col-span-2 text-right font-mono text-sm text-white/70">+{h.diff_pct}%</div>
              <div className="col-span-2 text-right font-mono text-xs text-white/50">
                {new Date(h.breakout_time_ist).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: false })}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────  SETTINGS (multi-scanner)  ───────────────────────── */
function Chapter06Settings({ scanners = [], onSave, onToggle, onRemove, busy }) {
  const [url, setUrl] = useState("");
  const [name, setName] = useState("");
  const [botTok, setBotTok] = useState("");
  const [chatId, setChatId] = useState("");
  const [showTg, setShowTg] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!url) return;
    const ok = await onSave({
      scanner_url: url,
      name: name || undefined,
      tg_bot_token: (botTok || undefined),
      tg_chat_id: (chatId || undefined),
    });
    if (ok) { setUrl(""); setName(""); setBotTok(""); setChatId(""); setShowTg(false); }
  };

  return (
    <section id="settings" className="relative py-24 lg:py-36 border-t border-white/8" data-testid="settings-section">
      <div className="mx-auto max-w-[1600px] px-6 lg:px-10">
        <ChapterHeader n="06" title="Every screener, every channel"
                       subtitle="Add as many Chartink scanners as you like. Route each to its own Telegram bot — or share one." />

        <div className="grid grid-cols-1 lg:grid-cols-[1.35fr_1fr] gap-12 items-start">
          {/* Left · list + add form */}
          <div className="space-y-8">
            {/* Existing scanners */}
            <div>
              <div className="flex items-baseline justify-between mb-4">
                <h3 className="font-editorial text-2xl lg:text-3xl">Subscribed scanners</h3>
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/45">
                  {scanners.length} configured
                </div>
              </div>
              <div className="space-y-3" data-testid="scanner-list">
                {scanners.length === 0 && (
                  <div className="text-sm text-white/45 font-mono">No scanners yet — add one below.</div>
                )}
                {scanners.map((s) => (
                  <ScannerRow key={s.slug} s={s} onToggle={onToggle} onRemove={onRemove} />
                ))}
              </div>
            </div>

            {/* Add form */}
            <form onSubmit={submit} className="card-surface rounded-2xl p-7" data-testid="add-scanner-form">
              <h3 className="font-editorial text-2xl mb-4">Add a scanner</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/50">Chartink URL or slug</label>
                  <input
                    data-testid="add-scanner-url"
                    value={url} onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://chartink.com/screener/volume-breakout"
                    className="mt-2 w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 focus:border-[#00E5FF]/50 focus:outline-none text-sm text-white placeholder:text-white/30 font-mono"
                  />
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/50">Display name (optional)</label>
                  <input
                    data-testid="add-scanner-name"
                    value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Volume Breakout"
                    className="mt-2 w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 focus:border-[#00E5FF]/50 focus:outline-none text-sm text-white placeholder:text-white/30 font-mono"
                  />
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/50">Route alerts to</label>
                  <button type="button" onClick={() => setShowTg((v) => !v)}
                          className="mt-2 w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 hover:border-white/30 text-sm text-left text-white/70 font-mono transition-colors"
                          data-testid="toggle-tg-override">
                    {showTg ? "Custom Telegram bot ↓" : "Default global bot · click to override →"}
                  </button>
                </div>
                <AnimatePresence>
                  {showTg && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/50">Bot token</label>
                        <input
                          data-testid="add-scanner-tg-token"
                          value={botTok} onChange={(e) => setBotTok(e.target.value)}
                          placeholder="1234567890:AAH…"
                          className="mt-2 w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 focus:border-[#FFB020]/50 focus:outline-none text-sm text-white placeholder:text-white/30 font-mono"
                        />
                      </div>
                      <div>
                        <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/50">Chat ID</label>
                        <input
                          data-testid="add-scanner-tg-chat"
                          value={chatId} onChange={(e) => setChatId(e.target.value)}
                          placeholder="e.g. 987654321"
                          className="mt-2 w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 focus:border-[#FFB020]/50 focus:outline-none text-sm text-white placeholder:text-white/30 font-mono"
                        />
                      </div>
                      <p className="md:col-span-2 font-mono text-[10px] text-white/45">
                        Leave blank to reuse the default bot (@optionswithabhijeet_bot).
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button type="submit" disabled={busy || !url}
                        className="pill-btn-primary inline-flex items-center gap-2 disabled:opacity-50"
                        data-testid="add-scanner-submit">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
                  Point &amp; verify
                </button>
              </div>
            </form>
          </div>

          {/* Right · editorial quote */}
          <div className="lg:pt-4">
            <p className="font-editorial text-3xl lg:text-4xl leading-tight text-white/85">
              &ldquo;A great screener writes half the trade. We route the other half.&rdquo;
            </p>
            <div className="mt-6 flex items-center gap-3 font-mono text-[10px] tracking-[0.3em] uppercase text-white/40">
              <span className="h-px w-8 bg-white/30" /> House rule N°02
            </div>
            <div className="mt-10 card-surface rounded-2xl p-6 space-y-3">
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/45">How multi-scanner works</div>
              <p className="text-sm text-white/60 leading-relaxed">
                Every <span className="text-white">:13 · :28 · :43 · :58 IST</span> we fire every <b>active</b> scanner in
                parallel. Stocks accumulate into a single watchlist. When a 5-minute close
                breaks the stored high, the alert goes to <span className="text-white">that scanner&apos;s</span> bot chat.
              </p>
              <p className="text-sm text-white/60 leading-relaxed">
                Pause a scanner to stop fetching it without losing its config. Delete
                to remove entirely (existing watchlist entries stay for today).
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ScannerRow({ s, onToggle, onRemove }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ duration: 0.4 }}
      className={`card-surface rounded-xl p-4 lg:p-5 border ${s.active ? "border-white/10" : "border-white/6 opacity-60"}`}
      data-testid={`scanner-row-${s.slug}`}
    >
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <span className={`h-2 w-2 rounded-full ${s.active ? "bg-[#39FF14]" : "bg-white/25"}`} />
          <div>
            <div className="font-editorial text-xl leading-tight">{s.name}</div>
            <a href={`https://chartink.com/screener/${s.slug}`} target="_blank" rel="noreferrer"
               className="font-mono text-[10px] tracking-widest text-white/40 hover:text-[#00E5FF] transition-colors">
              chartink.com/{s.slug} <ExternalLink className="inline h-2.5 w-2.5 ml-0.5" />
            </a>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {s.tg_bot_token ? (
            <span className="ticker-badge text-[#FFB020] border-[#FFB020]/30 bg-[#FFB020]/8">CUSTOM BOT</span>
          ) : (
            <span className="ticker-badge text-white/50">DEFAULT BOT</span>
          )}
          <button
            data-testid={`toggle-${s.slug}`}
            onClick={() => onToggle(s.slug, !s.active)}
            className="pill-btn text-xs">
            {s.active ? "Pause" : "Activate"}
          </button>
          <button
            data-testid={`remove-${s.slug}`}
            onClick={() => onRemove(s.slug)}
            className="pill-btn text-xs text-white/60 hover:text-[#FF3B30]">
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

/* ─────────────────────────  FOOTER  ───────────────────────── */
function FooterBar({ dash }) {
  const t = dash?.last_scan_time_ist ? new Date(dash.last_scan_time_ist).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: false }) : "—";
  return (
    <footer className="border-t border-white/8 py-10 text-white/40 font-mono text-[10px] tracking-[0.3em] uppercase">
      <div className="mx-auto max-w-[1600px] px-6 lg:px-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <span>Psychlevel Screener · Chartink 15m → 5m</span>
        <span>Last scan · {t} IST</span>
        <Link to="/" className="hover:text-white inline-flex items-center gap-2">
          Back to the lab <ChevronRight className="h-3 w-3" />
        </Link>
      </div>
    </footer>
  );
}

function ChapterHeader({ n, title, subtitle }) {
  return (
    <div className="mb-12 lg:mb-16">
      <div className="flex items-center gap-4 mb-4">
        <span className="h-px w-14 bg-white/25" />
        <span className="font-mono text-[10px] tracking-[0.4em] uppercase text-white/55">
          Chapter {n}
        </span>
      </div>
      <h2 className="font-editorial text-5xl lg:text-7xl leading-[0.98] tracking-tight max-w-3xl">
        {title}
      </h2>
      {subtitle && (
        <p className="mt-4 max-w-xl text-white/50 text-sm lg:text-base font-mono tracking-wide">
          {subtitle}
        </p>
      )}
    </div>
  );
}
